/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @LastEditors: Ryan
 * @LastEditTime: 2026-01-19
 */
import dynamic from 'next/dynamic';
import React from 'react';

import ProductAvatarSkeleton from '@/3.0/ai-product-avatar/_components/product-avatar-skeleton';
import { Introduction } from '@/components/container';
import DelayedRender from '@/components/DelayRender';
import Footer from '@/components/Footer';
import Layout from '@/components/layout';
import SEOHead from '@/landing/components/SEOHead';
import { createSoftwareAppSchema } from '@/landing/utils/structuredData';

const ProductAvatar = dynamic(() => import('../../3.0/ai-product-avatar/entry'), {
  ssr: false,
  loading: () => (
    <DelayedRender>
      <ProductAvatarSkeleton />
    </DelayedRender>
  ),
});

const SEOSections = dynamic(() => import('../../3.0/ai-product-avatar/seo'), {
  ssr: true,
});

// SEO Constants
const PAGE_TITLE = 'AI Product Avatar Video Generator for E-Commerce | FalcoCut';
const META_DESCRIPTION =
  'Turn product photos into professional video ads with AI avatars. Create TikTok Shop, Amazon, and social commerce videos in minutes—no filming, no actors, no studio needed.';
const CURRENT_URL = 'https://falcocut.ai/ai-product-avatar';

// Structured Data
const structuredData = createSoftwareAppSchema({
  name: 'FalcoCut AI Product Avatar',
  description: META_DESCRIPTION,
  url: CURRENT_URL,
  ratingValue: '4.9',
  ratingCount: '1850',
  featureList: [
    'Product photo to video conversion',
    'AI avatar product presenters',
    'TikTok Shop video creation',
    'Amazon listing videos',
    'Multi-language product marketing',
    'UGC-style product videos',
    'Batch video generation',
  ],
});

const ProductAvatarPage: React.FC & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  getExtraContent?: () => React.ReactNode;
} = () => {
  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={META_DESCRIPTION}
        url={CURRENT_URL}
        imageAlt="FalcoCut AI Product Avatar Generator"
        publishedTime="2026-01-19"
        modifiedTime="2026-01-19"
        structuredData={structuredData}
      />

      <ProductAvatar />
    </>
  );
};

// Define getLayout to return the page with Layout
ProductAvatarPage.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;

// Define SEO sections and Footer as extra content, rendered outside Layout
ProductAvatarPage.getExtraContent = () => {
  return (
    <Introduction>
      <SEOSections />
      <Footer />
    </Introduction>
  );
};

export const getStaticProps = async () => {
  return {
    props: {},
  };
};

export default ProductAvatarPage;
